import ExcelJS from 'exceljs';
import dayjs from 'dayjs';

export const ROLE_RATIO_REPORT_SHEET_NAME = 'Role Ratio Report';
export const PROJECT_ROLE_RATIO_SHEET_NAME = 'Project Role Ratio';

export const PROJECT_ROLE_RATIO_COLUMNS = [
  { field: 'projectId', header: 'Project Id', width: 18 },
  { field: 'projectName', header: 'Project Name', width: 32 },
  { field: 'projectVertical', header: 'Project Vertical', width: 24 },
  { field: 'projectType', header: 'Project Type', width: 20 },
  { field: 'projectStatus', header: 'Project Status', width: 20 },
  { field: 'baselineStartDate', header: 'Baseline Start Date', width: 22 },
  { field: 'baselineEndDate', header: 'Baseline End Date', width: 22 },
  { field: 'actualStartDate', header: 'Actual Start Date', width: 22 },
  { field: 'replannedEndDate', header: 'Replanned End Date', width: 22 },
  { field: 'projectManager', header: 'Sr. Manager/Project Manager', width: 34 },
  { field: 'role1', header: 'Role1', width: 18 },
  { field: 'role2', header: 'Role2', width: 18 },
  { field: 'actualRatio', header: 'Actual Ratio', width: 18 },
  { field: 'employeeCountRatio', header: 'Number Of Employee Count', width: 26 },
];

const PROJECT_ROLE_RATIO_DATE_FIELDS = new Set([
  'baselineStartDate',
  'baselineEndDate',
  'actualStartDate',
  'replannedEndDate',
]);

const FILTER_SELECTION_KEYS = [
  'selectedValues',
  'selectedValue',
  'selectedLabel',
  'value',
  'valueTo',
  'filterValue',
  'range',
  'inputValue',
  'condition',
  'startDate',
  'endDate',
];

const sanitizeSheetName = (value, fallback) => {
  const cleaned = String(value || fallback || 'Sheet')
    .replace(/[:\\/?*[\]]/g, ' ')
    .trim()
    .slice(0, 31);

  return cleaned || fallback || 'Sheet';
};

const formatRoleRatioExportDate = (value) => {
  if (value === null || value === undefined || value === '') return '';

  const parsedDate = dayjs(value);
  return parsedDate.isValid() ? parsedDate.format('DD-MMM-YYYY') : value;
};

const formatProjectRoleRatioRows = (rows = []) => (
  (Array.isArray(rows) ? rows : []).map((row) => (
    PROJECT_ROLE_RATIO_COLUMNS.reduce((acc, column) => ({
      ...acc,
      [column.field]: PROJECT_ROLE_RATIO_DATE_FIELDS.has(column.field)
        ? formatRoleRatioExportDate(row?.[column.field])
        : row?.[column.field],
    }), {})
  ))
);

const cloneFilterValue = (value) => {
  if (Array.isArray(value)) {
    return value.map((item) => cloneFilterValue(item));
  }

  if (value && typeof value === 'object') {
    return { ...value };
  }

  return value;
};

const normalizeFilterSelection = (filterItem = {}) => {
  const normalizedSelection = {};

  FILTER_SELECTION_KEYS.forEach((key) => {
    if (filterItem[key] !== undefined) {
      normalizedSelection[key] = cloneFilterValue(filterItem[key]);
    }
  });

  if (Array.isArray(normalizedSelection.selectedValues)) {
    normalizedSelection.selectedValues = normalizedSelection.selectedValues.filter(
      (item) => item !== null && item !== undefined && item !== '',
    );
  }

  if (
    normalizedSelection.selectedValue !== undefined
    && (!Array.isArray(normalizedSelection.selectedValues) || normalizedSelection.selectedValues.length === 0)
    && normalizedSelection.selectedValue !== null
    && normalizedSelection.selectedValue !== ''
  ) {
    normalizedSelection.selectedValues = [cloneFilterValue(normalizedSelection.selectedValue)];
  }

  return normalizedSelection;
};

export const getRoleRatioFiltersFromTableState = (tableState = {}, fallbackFilters = []) => {
  const stateFilters = Array.isArray(tableState?.filters) ? tableState.filters : [];
  if (stateFilters.length > 0) return stateFilters;
  return Array.isArray(fallbackFilters) ? fallbackFilters : [];
};

export const hasRoleRatioRole1Filter = (filters = []) => (
  (Array.isArray(filters) ? filters : []).some((filterItem) => {
    if (filterItem?.field !== 'role1Id') return false;

    if (Array.isArray(filterItem?.selectedValues) && filterItem.selectedValues.length > 0) {
      return true;
    }

    return filterItem?.selectedValue !== null
      && filterItem?.selectedValue !== undefined
      && filterItem?.selectedValue !== '';
  })
);

export const mergeRoleRatioFilterSelections = (tableConfiguration = [], filters = []) => {
  const selectionByField = new Map(
    (Array.isArray(filters) ? filters : [])
      .filter((filterItem) => filterItem?.field)
      .map((filterItem) => [filterItem.field, normalizeFilterSelection(filterItem)]),
  );

  return (Array.isArray(tableConfiguration) ? tableConfiguration : []).map((column) => ({
    ...column,
    menuItems: (Array.isArray(column?.menuItems) ? column.menuItems : []).map((menuItem) => ({
      ...menuItem,
      items: (Array.isArray(menuItem?.items) ? menuItem.items : []).map((item) => {
        if (item?.type !== 'filter' || !item?.field || !selectionByField.has(item.field)) {
          return item;
        }

        return {
          ...item,
          ...selectionByField.get(item.field),
        };
      }),
    })),
  }));
};

const createWorksheet = ({ workbook, sheetName, tableConfiguration = [], tableData = [] }) => {
  const worksheet = workbook.addWorksheet(sanitizeSheetName(sheetName, 'Sheet'));
  const columns = (Array.isArray(tableConfiguration) ? tableConfiguration : []).map((column) => ({
    header: column.header,
    key: column.field,
    width: Math.max(15, Math.min(40, Number(column.width) || 24)),
  }));

  worksheet.columns = columns;
  worksheet.getRow(1).font = { bold: true };
  (Array.isArray(tableData) ? tableData : []).forEach((row) => {
    const orderedRow = columns.reduce((acc, column) => {
      acc[column.key] = row?.[column.key] ?? '';
      return acc;
    }, {});
    worksheet.addRow(orderedRow);
  });

  return worksheet;
};

export const buildRoleRatioExportSheets = (exportPayload = {}) => [
  {
    sheetName: ROLE_RATIO_REPORT_SHEET_NAME,
    tableConfiguration: Array.isArray(exportPayload?.tableConfiguration) ? exportPayload.tableConfiguration : [],
    tableData: Array.isArray(exportPayload?.tableData) ? exportPayload.tableData : [],
  },
  {
    sheetName: PROJECT_ROLE_RATIO_SHEET_NAME,
    tableConfiguration: PROJECT_ROLE_RATIO_COLUMNS,
    tableData: formatProjectRoleRatioRows(exportPayload?.projectRows),
  },
];

export const hasRoleRatioExportData = (exportPayload = {}) => {
  const primaryRows = Array.isArray(exportPayload?.tableData) ? exportPayload.tableData : [];
  const secondaryRows = Array.isArray(exportPayload?.projectRows) ? exportPayload.projectRows : [];
  return primaryRows.length > 0 || secondaryRows.length > 0;
};

export const createRoleRatioWorkbook = ({ fileName, exportPayload }) => {
  const workbook = new ExcelJS.Workbook();
  workbook.creator = 'Darpan 2';
  workbook.created = new Date();
  workbook.modified = new Date();
  workbook.title = fileName;

  buildRoleRatioExportSheets(exportPayload).forEach((sheet) => {
    createWorksheet({
      workbook,
      sheetName: sheet.sheetName,
      tableConfiguration: sheet.tableConfiguration,
      tableData: sheet.tableData,
    });
  });

  return workbook;
};

export const downloadRoleRatioWorkbook = async ({ fileName, exportPayload }) => {
  const workbook = createRoleRatioWorkbook({ fileName, exportPayload });
  const buffer = await workbook.xlsx.writeBuffer();
  const blob = new Blob([buffer], {
    type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet',
  });
  const link = document.createElement('a');
  link.href = URL.createObjectURL(blob);
  link.download = `${fileName}.xlsx`;
  document.body.appendChild(link);
  link.click();
  document.body.removeChild(link);
  URL.revokeObjectURL(link.href);
};

export const exportRoleRatioWorkbook = async ({
  filters,
  userInfo,
  requestExport,
  buildFileName,
  onValidationError,
  onEmpty,
  onError,
  onSuccess,
  downloadWorkbook = downloadRoleRatioWorkbook,
}) => {
  if (!hasRoleRatioRole1Filter(filters)) {
    onValidationError?.('Please select Role 1 before exporting.');
    return { status: 'validation-error' };
  }

  try {
    const response = await requestExport({
      filter: filters,
      userInfo,
    });

    if (!response?.data?.success) {
      onError?.(response?.data?.message || 'Download Failed');
      return { status: 'error', response };
    }

    const exportPayload = response?.data?.data || {};
    if (!hasRoleRatioExportData(exportPayload)) {
      onEmpty?.('There is no data available for export');
      return { status: 'empty', response };
    }

    const fileName = buildFileName();
    await downloadWorkbook({ fileName, exportPayload });
    onSuccess?.(response);
    return { status: 'success', response };
  } catch (error) {
    onError?.('Download Failed');
    return { status: 'error', error };
  }
};
